/**
 * TierFeaturesPopup — admin popup to toggle COGS-contributing
 * features per tier and see their associated cost.
 *
 * Opens when admin clicks "Edit Features" on a tier card in PricingPanel.
 */
import { useState, useEffect } from 'react';
import { X, DollarSign, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Switch } from '../../components/ui/switch';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';

const GROUPS = [
  { label: 'AI Writing',              keys: ['ai_write_chapters','ai_rewrite_chapter','bulk_draft_novel','editorial_suggestions_realtime','create_story_bible_from_synopsis','convert_to_script'] },
  { label: 'World-Building & Coach',  keys: ['world_builder_deduce','claude_chat','claude_coach','revision_review'] },
  { label: 'Production',              keys: ['audiobook_generation','audiobook_voice_cloning','adobe_pdf_export','canva_cover_gen','copyscape_scan'] },
  { label: 'Publishing',              keys: ['publish_to_bookstore','royalties_dashboard','isbn_manager','marketing_panel','heat_map'] },
  { label: 'Human Services',          keys: ['writer_coaching','monthly_editor_review'] },
];

export function TierFeaturesPopup({ tier, onClose }) {
  const [featureMap, setFeatureMap] = useState(null);
  const [overrides,  setOverrides]  = useState({});
  const [saving,     setSaving]     = useState(false);

  useEffect(() => {
    fetch('/api/admin/plans/feature-map', { credentials: 'include' })
      .then(r => r.json())
      .then(d => {
        setFeatureMap(d);
        // Seed overrides from current tier caps
        const tierData = d.tiers.find(t => t.id === tier.id);
        setOverrides(tierData?.caps || {});
      })
      .catch(() => toast.error('Could not load feature map'));
  }, [tier.id]);

  async function handleSave() {
    setSaving(true);
    try {
      const res = await fetch(`/api/admin/plans/${tier.id}/features`, {
        method:      'PUT',
        credentials: 'include',
        headers:     { 'Content-Type': 'application/json' },
        body:        JSON.stringify({ overrides }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${tier.name} features saved`);
      onClose();
    } catch {
      toast.error('Save failed — try again');
    } finally {
      setSaving(false);
    }
  }

  const allFeatures = featureMap?.features || [];

  // Estimate monthly cost per active user
  const monthlyCostCents = allFeatures.reduce((sum, f) => {
    if (!overrides[f.key] || !f.cogs) return sum;
    // rough: 3 uses/mo average for session-frequency features
    const freq = ['ai_write_chapters','ai_rewrite_chapter','claude_chat'].includes(f.key) ? 8 : 2;
    return sum + f.cost_per_use_cents * freq;
  }, 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div
        data-testid="tier-features-popup"
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-zinc-900 border border-zinc-700/60 shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-4 bg-zinc-900 border-b border-zinc-800">
          <div>
            <h2 className="text-base font-semibold text-white">
              {tier.name} — Feature Controls
            </h2>
            <p className="text-xs text-zinc-500 mt-0.5">
              Toggle COGS-contributing features. Changes write to the DB override layer.
            </p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cost summary bar */}
        <div className="flex items-center gap-3 px-6 py-3 bg-zinc-800/60 border-b border-zinc-800 text-xs">
          <DollarSign className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-zinc-400">Estimated COGS per active user / month:</span>
          <span className="text-amber-300 font-mono font-semibold">
            ~${(monthlyCostCents / 100).toFixed(2)}
          </span>
          <span className="text-zinc-600 ml-auto">(based on typical usage frequency)</span>
        </div>

        {!featureMap && (
          <div className="py-16 flex justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-zinc-500" />
          </div>
        )}

        {featureMap && (
          <div className="px-6 py-4 space-y-6">
            {GROUPS.map(({ label, keys }) => {
              const features = keys
                .map(k => allFeatures.find(f => f.key === k))
                .filter(Boolean);
              if (!features.length) return null;
              return (
                <div key={label}>
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">
                    {label}
                  </p>
                  <div className="space-y-1">
                    {features.map(feat => (
                      <div
                        key={feat.key}
                        data-testid={`feature-toggle-${feat.key}`}
                        className={cn(
                          'flex items-center justify-between rounded-lg px-3 py-2 transition-colors',
                          overrides[feat.key]
                            ? 'bg-zinc-800/70 hover:bg-zinc-800'
                            : 'bg-zinc-800/30 hover:bg-zinc-800/50',
                        )}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-sm text-zinc-200 truncate">{feat.label}</span>
                          {feat.cogs && (
                            <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-600/40 text-amber-500">
                              COGS
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-3 shrink-0 ml-4">
                          {feat.cost_per_use_cents > 0 ? (
                            <span className="text-[11px] text-zinc-400 font-mono">
                              ${(feat.cost_per_use_cents / 100).toFixed(2)}
                              <span className="text-zinc-600"> /{feat.cadence}</span>
                            </span>
                          ) : (
                            <span className="text-[11px] text-zinc-600">—</span>
                          )}
                          <Switch
                            checked={overrides[feat.key] === true}
                            onCheckedChange={v =>
                              setOverrides(prev => ({ ...prev, [feat.key]: v }))
                            }
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 flex justify-end gap-3 px-6 py-4 bg-zinc-900 border-t border-zinc-800">
          <Button variant="ghost" size="sm" onClick={onClose} className="text-zinc-400">
            Cancel
          </Button>
          <Button
            size="sm"
            data-testid="save-features-btn"
            onClick={handleSave}
            disabled={saving || !featureMap}
            className="bg-violet-600 hover:bg-violet-500 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            <span className="ml-1.5">Save features</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
